// =============================================================================
// Platform Shared Types
// Game-agnostic data models and wire payloads. A game's own types live in
// games/<game>/shared/types.ts (Word Clash: games/word-clash/shared/types.ts)
// and extend the extensible ones here (MatchRejoinPayload, the typed socket
// maps, DashboardMetrics) — framework/shared never imports from games/.
// =============================================================================

import { ClientEvents, ServerEvents } from './events';
import type { FunnelEventName } from './constants/telemetry';
import type { InMatchChatMessageId } from './constants/messaging';
import type { GiftId } from './constants/economy';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum AuthProvider {
  Guest = 'guest',
  Apple = 'apple',
  Google = 'google',
}

export enum Language {
  English = 'en',
  Turkish = 'tr',
}

export enum FriendshipStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Blocked = 'blocked',
}

export enum GameMode {
  Classic = 'classic',
  Categories = 'categories',
}

export enum MatchStatus {
  Matchmaking = 'matchmaking',
  InProgress = 'in_progress',
  Completed = 'completed',
  Abandoned = 'abandoned',
}

export enum TransactionType {
  EnergyPurchase = 'energy_purchase',
  CoinPurchase = 'coin_purchase',
  GemPurchase = 'gem_purchase',
  EnergyAdReward = 'energy_ad_reward',
  CoinAdReward = 'coin_ad_reward',
  JokerSpend = 'joker_spend',
  TimeSpend = 'time_spend',
  ChangeQuestionSpend = 'change_question_spend',
  /** A table gift bought for a seat (#793). Metadata: `{ matchId, giftId, toUserId }`. */
  GiftSpend = 'gift_spend',
  MatchWinReward = 'match_win_reward',
  MatchLossReward = 'match_loss_reward',
  /**
   * Legacy 5-rounds daily challenge reward. Repurposed by Economy Tuning
   * v1 as the daily-challenge grant (`docs/ECONOMY.md` §4.3) — kept
   * with the same wire value for backwards compatibility with existing
   * Transaction rows.
   */
  DailyBonus = 'daily_bonus',
  UnlimitedEnergyPurchase = 'unlimited_energy_purchase',
  IapGemPurchase = 'iap_gem_purchase',
  IapNoAdsForever = 'iap_no_ads_forever',
  /**
   * First-purchase `starter_pack` bundle (`docs/ECONOMY.md` §8.1). Single
   * Transaction row carries the bundle composition in `metadata` (gems,
   * coins, energy, cosmetic) — we deliberately do NOT split into one row
   * per currency because the bundle is sold as one indivisible SKU.
   */
  IapBundlePurchase = 'iap_bundle_purchase',
  /**
   * Negative balance adjustment written by `RefundService.processRefund`
   * on Apple `REFUND`/`REVOKE` and Google `voidedPurchase` webhooks
   * (`docs/ECONOMY.md` §6.4). The row's `amount` is the actual debit
   * applied to the user — clamped at the user's current balance so the
   * post-debit value never goes negative (D6). The original grant amount
   * lives in `metadata.originalAmount`. The refund pipeline writes one row
   * per refund event; bundle SKUs (`starter_pack`) collapse all currency
   * legs into a single row whose `metadata` records the per-currency split.
   */
  RefundReversal = 'refund_reversal',
  AdminGrant = 'admin_grant',
  // --- Economy Tuning v1 (`docs/ECONOMY.md` §8.1) -------------------------
  /** 7-day login streak coin grant (and day-7 gem bonus). */
  DailyLoginBonus = 'daily_login_bonus',
  /** First match win of the UTC day — bonus coin grant. */
  FirstWinOfDay = 'first_win_of_day',
  /** Post-match "watch ad to double rewards" doubled-reward grant. */
  RewardDoubler = 'reward_doubler',
}

/**
 * The `TransactionType`s a coin SPEND is recorded under — the only values
 * `EconomyService.spendCoins` accepts (#817). A grant or purchase type there
 * would write a debit row a reward/IAP reader counts as income. Narrowing the
 * parameter changes no persisted value: each member is the existing enum
 * string.
 */
export type SpendTransactionType =
  | TransactionType.JokerSpend
  | TransactionType.TimeSpend
  | TransactionType.ChangeQuestionSpend
  | TransactionType.GiftSpend;

export enum Currency {
  Energy = 'energy',
  Coins = 'coins',
  Gems = 'gems',
  RealMoney = 'real_money',
}

export enum MessageStatus {
  Sent = 'sent',
  Delivered = 'delivered',
  Read = 'read',
}

export enum PresenceStatus {
  Online = 'online',
  Offline = 'offline',
}

export enum ModerationReportReason {
  // 'abuse' and 'harassment' were removed from the selectable set (2026-07).
  // Legacy MessageReport rows may still carry them — the admin SPA renders
  // the raw stored string, and resolve-status updates don't re-validate the
  // reason field, so historical rows stay readable and actionable.
  InappropriateContent = 'inappropriate_content',
  Spam = 'spam',
  Other = 'other',
}

/**
 * Outcome of running a message through the profanity filter.
 * `none` → clean; `masked` → delivered with the matched words replaced by the
 * mask token (`****`).
 *
 * `blocked` is RETIRED as an outcome — the filter has one tier and never
 * rejects a message for its content. The member survives only so historical
 * `ModerationEvent` rows written under the old two-tier policy still read and
 * render in the admin flagged-messages view. Nothing writes it.
 */
export type ModerationAction = 'none' | 'masked' | 'blocked';

// ---------------------------------------------------------------------------
// Data Models (Section 5)
// ---------------------------------------------------------------------------

/**
 * Stable ids for the achievement catalog (see `ACHIEVEMENTS` in
 * `shared/constants`). Used for compile-time safety on the award + display
 * paths. `User.achievements` is intentionally typed `string[]` rather than
 * `AchievementId[]` so a stale client never crashes on a newer id.
 */
export type AchievementId = 'hot_streak' | 'word_nerd' | 'veteran' | 'champion';

/** Section 5.1 — User */
export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  authProvider: AuthProvider;
  authProviderId: string | null;
  avatarId: string;
  coins: number;
  gems: number;
  energy: number;
  maxEnergy: number;
  energyLastRefill: string; // ISO timestamp
  unlimitedEnergyExpires: string | null;
  noAdsExpires: string | null;
  /**
   * Non-consumable "Remove Ads Forever" flag (`no_ads_forever` IAP).
   * Suppresses interstitial ads only — rewarded ads continue to show
   * because they are opt-in and grant the user rewards. AppConfig kill
   * switches still apply (this flag is additive, not a bypass of admin
   * controls).
   */
  noAdsForever: boolean;
  /**
   * Lifetime count of successful IAP grants (any SKU) for this user. Bumped
   * atomically inside every IAP grant path. Drives the first-purchase
   * gating for the `starter_pack` SKU (eligible only when `=== 0`) and
   * cohort analytics. Server-authoritative — never trusted from the client.
   * See `docs/ECONOMY.md` §8.2.
   */
  totalIapPurchases: number;
  /**
   * Cosmetic frame inventory. v1 only ever contains `'founder'`, granted
   * by `starter_pack`. Append-only via `$addToSet`. See `docs/ECONOMY.md`
   * §0 D8 + §3.2.
   */
  cosmeticFrames: string[];
  totalWins: number;
  totalLosses: number;
  totalMatches: number;
  totalCorrectAnswers: number;
  /**
   * True once the user's one-shot guided tutorial match has been initiated
   * (set at match START, not completion, so a mid-match quit never re-arms
   * the free entry). Server-authoritative — the join_matchmaking tutorial
   * fast-path claims it atomically; the client only mirrors it for local
   * gating. Legacy rows predating this field hydrate as false.
   */
  hasCompletedTutorial: boolean;
  /**
   * Current consecutive-win streak. Incremented on a win, reset to 0 on a
   * loss or draw, by `settleMatchStats` (match settlement). Drives the `hot_streak`
   * achievement. Legacy rows predating this field hydrate as 0.
   */
  winStreak: number;
  /**
   * Unlocked achievement ids (append-only — see the `ACHIEVEMENTS` catalog
   * in `shared/constants`). Persisted via `$addToSet` so replays are no-ops.
   * Typed as `string[]` (not `AchievementId[]`) so an older client never
   * crashes on an id minted by a newer server.
   */
  achievements: string[];
  language: Language;
  createdAt: string;
  updatedAt: string;
  lastActiveAt: string;
  isBanned: boolean;
  // --- Economy Tuning v1 (`docs/ECONOMY.md` §8.2) -------------------------
  /** Current 7-day login bonus streak (1-7, advances on each claim). */
  dailyBonusStreak: number;
  /** ISO timestamp of the most recent daily-login bonus claim. */
  lastDailyBonusClaimedAt: string | null;
  /** ISO timestamp the player last claimed the first-win-of-the-day bonus. */
  firstWinOfDayClaimedAt: string | null;
  /** Progress toward the current UTC-day daily challenge (0..N). */
  dailyChallengeProgress: number;
  /** ISO timestamp of the most recent daily challenge claim. */
  dailyChallengeClaimedAt: string | null;
  /**
   * ISO timestamp of the UTC day the current `dailyChallengeProgress`
   * belongs to. Used by the server to lazily reset stale progress when
   * the player has never crossed the claim threshold.
   */
  dailyChallengeProgressDate: string | null;
}

/** Section 5.2 — Friendship */
export interface Friendship {
  id: string;
  userId: string;
  friendId: string;
  status: FriendshipStatus;
  createdAt: string;
}

/** Direct message conversation between two accepted friends (#308). */
export interface Conversation {
  id: string;
  /** Two-element sorted array — compound index guarantees uniqueness per pair. */
  participantIds: [string, string];
  lastMessageAt: string; // ISO timestamp
  lastMessagePreview?: string;
  /** Unread count for the *current* user (hydrated per-request from unreadCounts map). */
  unreadCount: number;
}

/** A direct message in a Conversation (#308). */
export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  status: MessageStatus;
  createdAt: string; // ISO timestamp
  /** Soft-delete marker set when a report or block hides the message. */
  deletedAt?: string;
  /**
   * User ids who have "liked" (heart-tapped) this message (#530). A
   * participant may toggle their like on the PEER's non-deleted messages
   * only — the server rejects own-message likes. Server-authoritative — the
   * client renders a heart badge when this is non-empty and reconciles to
   * this array on every `dm:like_updated`.
   */
  likedBy?: string[];
}

/** Section 5.4 — Match */
export interface Match {
  id: string;
  mode: GameMode;
  player1Id: string;
  player2Id: string;
  status: MatchStatus;
  winnerId: string | null;
  player1Score: number;
  player2Score: number;
  totalRounds: number;
  startedAt: string;
  endedAt: string | null;
}

/** Section 5.7 — Transaction */
export interface Transaction {
  id: string;
  userId: string;
  type: TransactionType;
  currency: Currency;
  amount: number; // positive = credit, negative = debit
  balanceAfter: number;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// WebSocket Payloads — Client → Server
// ---------------------------------------------------------------------------

/** Fields match Zod schema in server/src/websocket/schemas.ts — keep in sync */
export interface JoinMatchmakingPayload {
  mode?: GameMode;
  language?: Language;
  /**
   * The active game's chosen match option (Backgammon Clash: the race-to-N
   * points target). OPAQUE to the shell — it only carries whichever value
   * the GameUiModule declared. Absent means "the game's default"; a value the
   * server-side GameModule does not declare is REFUSED
   * (`INVALID_MATCH_OPTION`), never defaulted.
   */
  matchOption?: string | number;
}

export type CancelMatchmakingPayload = Record<string, never>;

export interface LeaveMatchPayload {
  matchId: string;
}

/**
 * Resume/resync request. `matchId` is the match the CLIENT believes it is in.
 *
 * It is optional because the menu's Resume button asks without context, but
 * gameplay callers MUST send it: when the server has no `InProgress` match it
 * uses this id to replay that match's terminal `match_end` (see
 * `GAME.MATCH_RESULT_REPLAY_WINDOW_MS`). Without an id the server cannot tell
 * "your match ended while you were away" from "you were never in a match", and
 * a client that missed its result stays stuck on the gameplay screen.
 */
export interface RejoinMatchPayload {
  matchId?: string;
  /**
   * Home's Resume on a table the player LEFT (`ActiveMatchSummary.reclaimable`,
   * #798): take the seat back. ONLY this explicit intent reclaims — a
   * reconnect, a foreground probe or a stall watchdog never does.
   */
  reclaim?: boolean;
}

export interface ChallengeFriendPayload {
  friendId: string;
  language?: Language;
  isRematch?: boolean;
}

export interface ChallengeRespondPayload {
  challengeId: string;
  accept: boolean;
}

// --- Friend tables (N-seat games, #772) ---

export interface TableInviteCreatePayload {
  /** 1..(seats - 1) accepted friends. */
  friendIds: string[];
  /** The game's match option, validated like `join_matchmaking.matchOption`. */
  matchOption?: string | number;
  /**
   * Team options only: who sits with the host — one of `friendIds`, or `null`
   * for the open seat (a stranger or a bot). Must be omitted for an option
   * without teams.
   */
  partnerId?: string | null;
  language?: Language;
}

export interface TableInviteRespondPayload {
  inviteId: string;
  accept: boolean;
}

export interface TableInviteDecidePayload {
  inviteId: string;
  /** true = start now, empty seats go to matchmaking; false = cancel the table. */
  proceed: boolean;
}

export interface TableInviteLeavePayload {
  inviteId: string;
}

// --- DMs (#308) ---

export interface DMSendPayload {
  recipientId: string;
  text: string;
  /** Optional client-generated id for optimistic UI dedupe. */
  clientMessageId?: string;
}

export interface DMReadPayload {
  conversationId: string;
}

export interface DMTypingStartPayload {
  conversationId: string;
}

export interface DMTypingStopPayload {
  conversationId: string;
}

// ---------------------------------------------------------------------------
// WebSocket Payloads — Server → Client
// ---------------------------------------------------------------------------

export interface OpponentInfo {
  id: string;
  username: string;
  displayName: string;
  avatarId: string;
  totalWins: number;
}

export interface MatchFoundPayload {
  matchId: string;
  opponent: OpponentInfo;
  /**
   * True when this is the player's one-shot guided tutorial match (instant
   * easy-bot pairing, free entry). Drives the mobile lesson overlays.
   * Omitted (never `false`) on ordinary matches. ACCEPTED TRADEOFF: every
   * tutorial match is bot-paired by construction, so this flag lets the
   * client infer the opponent is a bot for THIS match only. That inference
   * is deliberately tutorial-scoped — do not cite it as precedent for other
   * derived bot-identity flags; no `isBot`-family field may ever ride a
   * client payload.
   */
  isTutorial?: boolean;
}

/**
 * One already-seated player at a FORMING table, as the search screen renders
 * them. Deliberately the narrowest public shape in the codebase: a seat that
 * is about to be revealed needs a face and a name and nothing else.
 *
 * There is no bot marker here and there must never be one — the whole point
 * of the staggered fill is that a bot arriving is indistinguishable from a
 * person arriving (`02-server-rules.md` §Bot System Guards).
 */
export interface MatchmakingSeatOccupant {
  id: string;
  username: string;
  displayName: string;
  avatarId: string;
}

/**
 * Progressive occupancy of the table THIS player is waiting at
 * (#table-bot-seat-stagger).
 *
 * `occupants` is everyone seated so far EXCEPT the recipient, in ARRIVAL
 * order — the order the client fills its waiting slots in. It is append-only
 * across a fill, so a later payload never reorders an earlier one.
 *
 * Arrival order is NOT the final seat order: `initiateTable` shuffles seats
 * from the CSPRNG so queue order never decides who bids first. The reveal
 * re-sorts by the real seat once `match_found` lands.
 */
export interface MatchmakingSeatsPayload {
  /** Total seats at the table being formed. */
  seats: number;
  occupants: MatchmakingSeatOccupant[];
}

/**
 * One row of an N-seat table's final standings (epic #679). Rides
 * `MatchEndPayload.standings`, which a 2-seat game never sets.
 *
 * Deliberately carries NO bot-identity field (`07-game-design-rules.md`
 * §Bot Behavior) — `leftMatch` says a human's seat was played out for them,
 * which every player at the table watched happen, and nothing about who or
 * what played it.
 */
export interface TableStanding {
  seat: number;
  userId: string;
  username: string;
  displayName: string;
  avatarId: string;
  /** Team index for partnership formats; null for individual play. */
  team: number | null;
  /** 1-based, shared on ties (1, 1, 3, 4). */
  placement: number;
  /** Final game score as the game defines it. */
  score: number;
  isYou: boolean;
  /** This player disconnected past grace and their seat was played out. */
  leftMatch: boolean;
}

export interface MatchEndPayload {
  /**
   * Identity of the match this result belongs to. OPTIONAL because the
   * incumbent's emitters predate it; a game whose store guards against a
   * replayed tail (Socket.IO connectionStateRecovery re-delivering the
   * just-ended match's `match_end` after `match_found` switched matches)
   * MUST set it — without it the client cannot tell the old result from
   * the live match's. See backgammon `store.onMatchEnd`.
   */
  matchId?: string;
  yourScore: number;
  opponentScore: number;
  winner: 'you' | 'opponent' | 'draw';
  /**
   * Total coins granted to *this* player for the match. Equals
   * `baseCoins + answerBonusCoins + (firstWinOfDayBonus ? 50 : 0) +
   * (dailyChallengeBonusFired ? 100 : 0)`. Server-authoritative — the
   * client renders the breakdown but does not recompute the total.
   */
  coinsEarned: number;
  /**
   * Base reward for this player: 50 (win), 10 (loss/draw). Sourced from
   * `COINS.MATCH_WIN_REWARD` / `COINS.MATCH_LOSS_REWARD`.
   * Economy Tuning v1, `docs/ECONOMY.md` §4.5.
   */
  baseCoins: number;
  /**
   * Match-end effort bonus — the game's own formula (e.g. a per-correct-
   * answer bonus, a point margin), supplied by the game at settlement.
   * Granted to both winner and loser (encourages effort even on losing
   * matches). The field name is frozen wire contract.
   */
  answerBonusCoins: number;
  /** Player's correct-answer count across all rounds in this match. */
  correctAnswers: number;
  /**
   * `true` when the first-win-of-the-UTC-day bonus fired for this player on
   * this match (winners only). See `docs/ECONOMY.md` §4.2.
   */
  firstWinOfDayBonus: boolean;
  /**
   * Player's progress toward the 5-wins-per-UTC-day daily challenge after
   * this match (0..5). Caps at 5 once claimed.
   * See `docs/ECONOMY.md` §4.3.
   */
  dailyChallengeProgress: number;
  /**
   * UTC midnight of the day `dailyChallengeProgress` belongs to, ISO-encoded
   * (`null` when the player has no stamp — a legacy row, or a bot's fallback
   * envelope).
   *
   * Ships WITH the counter because the two are only meaningful together: the
   * client derives the displayed value from this stamp (#562), and a client
   * that synthesized the stamp from its own clock would resurrect the bug in
   * a narrower window — a match settled at 23:59:59.9 UTC and processed at
   * 00:00:00.1 would be re-stamped to the NEXT day, carrying the old day's
   * progress into it. The day boundary is server-authoritative; the client
   * mirrors this value verbatim and never re-derives it.
   */
  dailyChallengeProgressDate: string | null;
  /**
   * `true` when the daily-challenge bonus (+100 coins for 5 wins in a UTC
   * day) fired for this player on this match.
   */
  dailyChallengeBonusFired: boolean;
  /**
   * `true` when this player is eligible to claim the match-end "watch ad
   * to double coins" affordance (#416, `docs/ECONOMY.md` §4.4). The
   * server gates this on:
   *   - feature flag `COINS.MATCH_REWARD_DOUBLER_AD_ENABLED`
   *   - this player has a non-zero match-coin grant (base + answer bonus)
   *   - the opponent is not a bot — UX clarity, doublers don't appear for
   *     bot matches even though the math would technically be the same
   *   - this player is not a bot
   * The mobile client renders the doubler CTA only when this is `true`.
   */
  canDoubleCoins: boolean;
  /**
   * N-seat tables only (epic #679): every seat's final standing, in seat
   * order. When present, `winner` is `'you'` iff this player placed 1st (or
   * their team did), `yourScore` is their own score and `opponentScore` the
   * best score among the others — so a client that predates tables still
   * renders a coherent win/loss, and a table-aware client renders standings.
   */
  standings?: TableStanding[];
}

/**
 * `match_rejoin` — the platform half of a resync snapshot: which match, who
 * the opponent is, whose turn it is and the match score. A game that answers
 * `rejoin_match` with this event extends it with its own round state (Word
 * Clash: `WordClashMatchRejoinPayload` in games/word-clash/shared/types.ts).
 */
export interface MatchRejoinPayload {
  matchId: string;
  mode: GameMode;
  opponent: OpponentInfo;
  /** True when the rejoined match is the guided tutorial match (see {@link MatchFoundPayload.isTutorial}). */
  isTutorial?: boolean;
  isYourTurn: boolean;
  turnNumber: number;
  turnDeadlineMs: number;
  yourScore: number;
  opponentScore: number;
}

export interface OpponentDisconnectedPayload {}

/** Where one friend-table member stands. The host is always `host`. */
export type TableInviteMemberStatus = 'host' | 'pending' | 'accepted' | 'declined' | 'expired' | 'left';

export interface TableInviteMember {
  id: string;
  username: string;
  displayName: string;
  avatarId: string;
  status: TableInviteMemberStatus;
}

/**
 * The whole lobby, sent on every change (a snapshot, never a delta, so a
 * dropped packet self-heals on the next one).
 *  - `inviting`: waiting for answers until `respondBy`.
 *  - `awaitingHost`: nobody is pending and someone is missing — the host
 *    continues (their seats go to matchmaking) or cancels, by `decideBy`.
 */
export interface TableInviteSnapshot {
  inviteId: string;
  hostId: string;
  matchOption?: string | number;
  /** The language the table plays in (the host's). */
  language: Language;
  /** Present only for a team option: the host's partner, or `null` for the open seat. */
  partnerId?: string | null;
  /** Host first, then invitees in the order they were picked. */
  members: TableInviteMember[];
  phase: 'inviting' | 'awaitingHost';
  /** Epoch ms. */
  respondBy: number;
  /** Epoch ms; set in `awaitingHost`. */
  decideBy?: number;
  /**
   * The server's clock when this snapshot was sent. A client measures the
   * deadlines against IT, not its own clock, which may be off by minutes.
   */
  serverNow?: number;
}

/** `match_chat:send` (#792). `matchId` is the match the sender is looking at;
 *  the server refuses it unless it is the sender's own live match. */
export interface MatchChatSendPayload {
  matchId: string;
  messageId: InMatchChatMessageId;
}

/** `match_chat:received` — to every seat still at the table, sender included. */
export interface MatchChatReceivedPayload {
  matchId: string;
  fromUserId: string;
  messageId: InMatchChatMessageId;
}

/** `match_gift:send` (#793). `toUserId` may be the sender. */
export interface MatchGiftSendPayload {
  matchId: string;
  giftId: GiftId;
  toUserId: string;
}

/** `match_gift:received` — to every seat still at the table, sender included. */
export interface MatchGiftReceivedPayload {
  matchId: string;
  fromUserId: string;
  toUserId: string;
  giftId: GiftId;
  /** The SENDER's coin balance after the debit — present on the sender's copy only. */
  coinsAfter?: number;
}

// ---------------------------------------------------------------------------
// Yancı — watching a friend's live match (#934)
// ---------------------------------------------------------------------------

/** `spectate:start` — sit beside this friend as their Yancı. */
export interface SpectateStartPayload {
  friendId: string;
}

/** A Yancı (or the friend a Yancı watches), as the table shows them. */
export interface SpectatorInfo {
  id: string;
  username: string;
  displayName: string;
  avatarId: string;
}

/**
 * `spectate:started` — sent to the Yancı before the friend's view. The game's
 * own snapshot events follow; the client renders them as the friend's view,
 * read-only.
 */
export interface SpectateStartedPayload {
  matchId: string;
  /** The friend being watched — whose seat ("you") every following payload describes. */
  watched: SpectatorInfo;
}

/**
 * Why a Yancı seat ended. `refused` = `spectate:start` itself was refused; the
 * reason was already sent as the ordinary error message, so the client only
 * stops waiting.
 */
export type SpectateEndReason = 'match_over' | 'left' | 'unavailable' | 'refused';

/** `spectate:ended` — the Yancı seat is gone. */
export interface SpectateEndedPayload {
  matchId: string;
  reason: SpectateEndReason;
}

/** `spectate:spectators` — everyone watching this live match right now. */
export interface SpectatorsUpdatedPayload {
  matchId: string;
  spectators: SpectatorInfo[];
}

export interface TableInviteStartedPayload {
  inviteId: string;
  matchOption?: string | number;
  /**
   * Everyone else the party seats at this table (the recipient excluded), in
   * the order the fill seats them — so Matchmaking draws the friends from its
   * first frame instead of waiting for the first stranger's
   * `matchmaking_seats` (#791). Optional: an older server omits it.
   */
  occupants?: MatchmakingSeatOccupant[];
}

export type TableInviteClosedReason = 'host_cancelled' | 'host_left' | 'host_timeout' | 'failed';

export interface TableInviteClosedPayload {
  inviteId: string;
  reason: TableInviteClosedReason;
}

export interface ChallengeReceivedPayload {
  challengeId: string;
  challenger: {
    id: string;
    username: string;
    displayName: string;
    avatarId: string;
  };
  /**
   * Language the match will be played in (the challenger's UI language —
   * the same value the challenge stores for match creation). The client
   * shows a "game language" line in the Accept/Decline dialog ONLY when it
   * differs from the recipient's own UI language. Optional so an older
   * server payload simply hides the line.
   */
  language?: Language;
}

export interface ChallengeExpiredPayload {
  challengeId: string;
}

export interface ChallengeDeclinedPayload {
  challengeId: string;
}

// --- DMs (#308) — server → client ---

/** Public sender info inlined on incoming DMs for zero-fetch UI. */
export interface DMSender {
  id: string;
  displayName: string;
  avatarId: string;
  /**
   * True when the peer's account no longer exists (deleted account).
   * Conversations and messages are retained on purpose — the client renders
   * a localized "Deleted user" card and disables the composer.
   */
  deleted?: boolean;
}

export interface DMReceivedPayload {
  conversationId: string;
  message: Message;
  sender: DMSender;
  /**
   * Recipient's display card — present on the FIRST message of a brand-new
   * conversation. The sender's own echo carries `sender = the author`, so
   * without this the sender's device would seed the new conversation card
   * with ITSELF as the peer, and a thread opened via a `pending-{peerId}`
   * placeholder could never resolve to the real conversation (the first
   * message appeared only after a REST refresh).
   */
  recipient?: DMSender;
  /** Echo of clientMessageId from DMSendPayload (for the sender's other sessions). */
  clientMessageId?: string;
}

export interface DMConversationUpdatedPayload {
  conversationId: string;
  lastMessageAt: string;
  lastMessagePreview?: string;
  unreadCount: number;
}

export interface DMBlockedPayload {
  blockedUserId: string;
  blocked: boolean;
}

export interface DMTypingPayload {
  conversationId: string;
  userId: string;
  typing: boolean;
}

/**
 * Server → client: the reader has just marked the conversation read
 * (`dm:read` / REST markRead flipped the peer's messages to `read`).
 * Delivered to the message SENDER so their bubbles' status ticks flip to
 * "seen" in real time without a history refetch. The receiver flips every
 * message in the conversation NOT sent by `readerId`.
 */
export interface DMMessagesReadPayload {
  conversationId: string;
  /** The user who read the messages (the peer, from the receiver's view). */
  readerId: string;
}

// --- DM likes (#530) ---

/** Client → server: toggle the caller's like on a message. */
export interface DMLikePayload {
  conversationId: string;
  messageId: string;
  /** true = add the caller's like, false = remove it. */
  liked: boolean;
}

/** Server → client: the authoritative like set for a message, fanned out to
 *  both participants (and the liker's other sessions). */
export interface DMLikeUpdatedPayload {
  conversationId: string;
  messageId: string;
  likedBy: string[];
}

// --- Post-match quick chat ---

/**
 * Client → server: send a predefined quick-chat message to the opponent of a
 * FINISHED match. `messageId` must be one of `QUICK_CHAT.MESSAGE_IDS`
 * (`shared/constants/messaging.ts`) — the server rejects anything else, so no
 * free text ever rides this channel.
 */
export interface QuickChatSendPayload {
  matchId: string;
  messageId: string;
}

/**
 * Server → client: a quick-chat message from the match opponent. The client
 * renders `messageId` in its OWN UI language (mobile i18n `quickChat.messages`)
 * and ignores unknown ids (newer server than client).
 */
export interface QuickChatReceivedPayload {
  matchId: string;
  messageId: string;
}

// --- Presence (#308) ---

export interface PresenceSnapshotPayload {
  onlineFriendIds: string[];
}

export interface PresenceFriendOnlinePayload {
  userId: string;
}

export interface PresenceFriendOfflinePayload {
  userId: string;
}

/** A friend entered (`inMatch: true`) or left a live match (#934). */
export interface PresenceFriendStatusPayload {
  userId: string;
  inMatch: boolean;
}

export interface FriendAddedPayload {
  /** The OTHER user's id from the recipient's perspective — the newly-added friend. */
  friendId: string;
  /** Whether the newly-added friend is currently online. */
  isOnline: boolean;
  /** The newly-added friend's display name, for user-visible feedback. */
  friendDisplayName: string;
  /**
   * True when the receiving user is the one who initiated the friendship
   * (sent the request / generated the invite link). Clients use this to show
   * "X accepted your friend request" ONLY to the initiator — the accepter
   * already has direct UI feedback from tapping Accept.
   */
  initiatedByMe: boolean;
}

export interface FriendRequestReceivedPayload {
  /** The requesting user's id. */
  senderId: string;
  /** The requesting user's display name, for the toast / notification copy. */
  senderDisplayName: string;
  /** The requesting user's avatar id, when set. */
  senderAvatarId?: string;
}

// ---------------------------------------------------------------------------
// Typed Socket.IO Event Maps
// ---------------------------------------------------------------------------

/**
 * Server → Client event signatures for Socket.IO typed generics — the
 * platform events only. A game extends this with its gameplay events
 * (Word Clash: `WordClashServerToClientEvents`).
 */
export interface ServerToClientEvents {
  [ServerEvents.MATCH_FOUND]: (payload: MatchFoundPayload) => void;
  [ServerEvents.MATCH_END]: (payload: MatchEndPayload) => void;
  [ServerEvents.OPPONENT_DISCONNECTED]: () => void;
  [ServerEvents.CHALLENGE_RECEIVED]: (payload: ChallengeReceivedPayload) => void;
  [ServerEvents.CHALLENGE_EXPIRED]: (payload: ChallengeExpiredPayload) => void;
  [ServerEvents.CHALLENGE_DECLINED]: (payload: ChallengeDeclinedPayload) => void;
  [ServerEvents.MATCHMAKING_TIMEOUT]: (payload: Record<string, never>) => void;
  [ServerEvents.MATCHMAKING_CANCELLED]: (payload: Record<string, never>) => void;
  [ServerEvents.MATCH_REJOIN]: (payload: MatchRejoinPayload) => void;
  [ServerEvents.DM_RECEIVED]: (payload: DMReceivedPayload) => void;
  [ServerEvents.DM_CONVERSATION_UPDATED]: (payload: DMConversationUpdatedPayload) => void;
  [ServerEvents.DM_BLOCKED]: (payload: DMBlockedPayload) => void;
  [ServerEvents.DM_TYPING]: (payload: DMTypingPayload) => void;
  [ServerEvents.DM_LIKE_UPDATED]: (payload: DMLikeUpdatedPayload) => void;
  [ServerEvents.DM_MESSAGES_READ]: (payload: DMMessagesReadPayload) => void;
  [ServerEvents.QUICK_CHAT_RECEIVED]: (payload: QuickChatReceivedPayload) => void;
  [ServerEvents.PRESENCE_SNAPSHOT]: (payload: PresenceSnapshotPayload) => void;
  [ServerEvents.PRESENCE_FRIEND_ONLINE]: (payload: PresenceFriendOnlinePayload) => void;
  [ServerEvents.PRESENCE_FRIEND_OFFLINE]: (payload: PresenceFriendOfflinePayload) => void;
  [ServerEvents.PRESENCE_FRIEND_STATUS]: (payload: PresenceFriendStatusPayload) => void;
  [ServerEvents.FRIEND_ADDED]: (payload: FriendAddedPayload) => void;
  [ServerEvents.FRIEND_REQUEST_RECEIVED]: (payload: FriendRequestReceivedPayload) => void;
  error: (payload: { message: string }) => void;
}

/**
 * Client → Server event signatures for Socket.IO typed generics — the
 * platform events only. A game extends this with its gameplay events
 * (Word Clash: `WordClashClientToServerEvents`).
 */
export interface ClientToServerEvents {
  [ClientEvents.JOIN_MATCHMAKING]: (payload: JoinMatchmakingPayload) => void;
  [ClientEvents.CANCEL_MATCHMAKING]: () => void;
  [ClientEvents.LEAVE_MATCH]: (payload: LeaveMatchPayload) => void;
  [ClientEvents.REJOIN_MATCH]: (payload: RejoinMatchPayload) => void;
  [ClientEvents.CHALLENGE_FRIEND]: (payload: ChallengeFriendPayload) => void;
  [ClientEvents.CHALLENGE_RESPOND]: (payload: ChallengeRespondPayload) => void;
  [ClientEvents.DM_SEND]: (payload: DMSendPayload) => void;
  [ClientEvents.DM_READ]: (payload: DMReadPayload) => void;
  [ClientEvents.DM_TYPING_START]: (payload: DMTypingStartPayload) => void;
  [ClientEvents.DM_TYPING_STOP]: (payload: DMTypingStopPayload) => void;
  [ClientEvents.DM_LIKE]: (payload: DMLikePayload) => void;
  [ClientEvents.QUICK_CHAT_SEND]: (payload: QuickChatSendPayload) => void;
}

// ---------------------------------------------------------------------------
// API Request/Response Types (Section 14)
// ---------------------------------------------------------------------------

/** POST /auth/guest */
export interface GuestAuthRequest {
  deviceId?: string;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  user: User;
}

/** POST /auth/social */
export interface SocialAuthRequest {
  provider: Exclude<AuthProvider, AuthProvider.Guest>;
  idToken: string;
}

/** POST /auth/link */
export interface LinkAccountRequest {
  provider: Exclude<AuthProvider, AuthProvider.Guest>;
  idToken: string;
}

/** PATCH /users/me */
export interface UpdateUserRequest {
  displayName?: string;
  avatarId?: string;
  language?: Language;
  soundEnabled?: boolean;
  musicEnabled?: boolean;
  notificationsEnabled?: boolean;
}

/** GET /users/me response (extends User with computed fields) */
export interface UserProfile extends User {
  energyRegenSecondsRemaining: number;
  hasUnlimitedEnergy: boolean;
  hasNoAds: boolean;
}

/** Relationship between the requesting viewer and a public profile */
export type PublicProfileFriendship = 'none' | 'friends' | 'request_sent' | 'request_received';

/** GET /users/:id public profile */
export interface PublicProfile {
  id: string;
  username: string;
  displayName: string;
  avatarId: string;
  totalWins: number;
  totalMatches: number;
  totalCorrectAnswers: number;
  /**
   * Unlocked achievement badges. Public: a badge is a bragging right, so any
   * viewer sees the same grid the owner does (locked tiles come from the
   * client's `ACHIEVEMENT_IDS` catalog, not from here). Typed `string[]` to
   * match `User.achievements` — a stale client must not crash on a newer id.
   */
  achievements: string[];
  /** ISO account-creation date, shown as "member since" */
  createdAt: string;
  /**
   * Viewer-relative friendship state so the profile's action button renders
   * correctly on first paint (blocked rows intentionally read as 'none').
   */
  friendship: PublicProfileFriendship;
}

/** POST /shop/purchase */
export interface ShopPurchaseRequest {
  productId: string;
  iapReceipt?: string; // for real-money purchases
}

export interface ShopPurchaseResponse {
  transaction: Transaction;
  updatedBalances: {
    energy: number;
    coins: number;
    gems: number;
  };
}

/** POST /shop/ad-reward */
export interface AdRewardRequest {
  rewardType: 'energy' | 'coins';
  adNetworkVerification: string;
}

/** GET /shop/products */
export interface ShopProduct {
  id: string;
  name: string;
  description: string;
  currency: Currency;
  amount: number;
  cost: number;
  costCurrency: Currency | 'ad';
  priceUsd?: number; // for IAP
}

/** GET /matches/history */
export interface MatchHistoryItem {
  matchId: string;
  /**
   * The single opponent of a 2-seat match. For an N-seat table (epic #679)
   * this is the highest-placed OTHER player, so an older client still renders
   * a row; `opponents` carries the full table.
   */
  opponent: PublicProfile;
  /** N-seat tables only: every other player, in seat order. */
  opponents?: PublicProfile[];
  /** N-seat tables only: this player's final placement (1-based). */
  placement?: number;
  /** N-seat tables only: seats at the table. */
  playerCount?: number;
  mode: GameMode;
  yourScore: number;
  opponentScore: number;
  result: 'win' | 'loss' | 'draw';
  /**
   * The match ended with no result — abandoned and refunded, nobody placed
   * (`isVoidedMatch`). `result` then reads `'draw'` for older clients; a
   * current client renders "Abandoned" instead of a 0-0 draw.
   */
  abandoned?: boolean;
  endedAt: string;
}

/** The profile fields `GET /matches/:id` sends for each player (`serializeUserPublic`). */
export type MatchParticipantProfile = Pick<
  PublicProfile,
  'id' | 'username' | 'displayName' | 'avatarId' | 'totalWins' | 'totalMatches'
>;

/** One seat of an N-seat table in `GET /matches/:id` (epic #679). */
export interface MatchDetailSeat {
  seat: number;
  /** Null when the account no longer resolves (deleted). */
  player: MatchParticipantProfile | null;
  /** Team index for partnership formats; null for individual play. */
  team: number | null;
  /** 1-based, shared on ties; null when the table ended unplaced. */
  placement: number | null;
  score: number;
  /**
   * The settled outcome when the game stated one a placement cannot express
   * (an eşli exact tie); a seat that left always reads `'loss'`, as settlement
   * scored it. Null = derive from `placement`.
   */
  outcome: 'win' | 'loss' | 'draw' | null;
  /** The player left and their seat was played out. */
  leftMatch: boolean;
  isYou: boolean;
}

/**
 * One round of a 2-seat match that records rounds (today only Word Clash).
 * Stays a PLATFORM type: it is part of the frozen `GET /matches/:id`
 * response the shared MatchDetail screen renders, and its shape (round
 * points + a turn-ordered feed of text entries) carries no question or
 * power-up concept — a game that keeps no rounds sends `rounds: []`.
 */
export interface MatchDetailRound {
  roundNumber: number;
  yourPoints: number;
  opponentPoints: number;
  winnerId: string | null;
  answers: {
    userId: string;
    answerText: string;
    isValid: boolean;
    turnNumber: number;
    isYours: boolean;
  }[];
}

/** GET /matches/:id — a finished (or live) match from the caller's seat. */
export interface MatchDetailResponse {
  matchId: string;
  mode: GameMode;
  status: MatchStatus;
  /** 2-seat: the opponent. N-seat: the best-placed other player. */
  opponent: MatchParticipantProfile | null;
  yourScore: number;
  opponentScore: number;
  winnerId: string | null;
  result: 'win' | 'loss' | 'draw';
  /** See {@link MatchHistoryItem.abandoned}. */
  abandoned: boolean;
  /** Empty for a game that keeps no Round rows (every N-seat game). */
  rounds: MatchDetailRound[];
  /** N-seat tables only: every seat, in seat order. */
  seats?: MatchDetailSeat[];
  startedAt: string;
  endedAt: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

/**
 * GET /matches/active — the caller's InProgress match, if any.
 * Drives the Home screen's Play→Resume button; the full snapshot to actually
 * re-enter the match arrives over WebSocket via the `rejoin_match` event.
 */
export interface ActiveMatchSummary {
  matchId: string;
  mode: GameMode;
  opponent: PublicProfile | null;
  yourScore: number;
  opponentScore: number;
  startedAt: string;
  /**
   * The player LEFT this table and the server is playing their seat (#798);
   * Resume hands it back. Absent for a match they are still bound to.
   */
  reclaimable?: boolean;
}

export interface ActiveMatchResponse {
  match: ActiveMatchSummary | null;
}

/** POST /friends/request */
export interface FriendRequestPayload {
  userId: string;
}

/** GET /leaderboard/global or /leaderboard/friends */
export interface LeaderboardRow {
  rank: number;
  userId: string;
  username: string;
  displayName: string;
  avatarId: string;
  wins: number;
  isCurrentUser: boolean;
}

/** GET /leaderboard/global response envelope */
export interface GlobalLeaderboardResponse extends PaginatedResponse<LeaderboardRow> {
  currentUserRank: number | null;
  /**
   * The caller's own row, present even when they fall outside the returned
   * page, so the client can pin or auto-scroll to it.
   *
   * `null` in two cases: the caller has no recorded win yet, OR the requested
   * page lies entirely past the top-N cap (deriving a rank there costs an
   * unbounded count, so the server declines). Page 1 is the canonical source
   * for the pinned row — it always carries the caller's rank when they are on
   * the board. `currentUserRank` follows the same rules.
   */
  currentUserEntry: LeaderboardRow | null;
}

// ---------------------------------------------------------------------------
// Admin role tier (server/src/middleware/auth.ts adminMiddleware(minRole))
// ---------------------------------------------------------------------------

/**
 * Admin role tier. Every row in the AdminUser collection has a role — there
 * is no `'none'` sentinel because players and admins live in SEPARATE
 * collections (`User` vs `AdminUser`), so "not an admin" is expressed by
 * the absence of an `AdminUser` record, not by a role value.
 *
 * `viewer < admin < superadmin` is the LINEAR tier ladder — a higher tier
 * satisfies every lower-tier guard (`requireAdminRole` / `RequireAdmin`).
 *
 * `content_manager` is deliberately OUTSIDE that ladder. It is a LATERAL,
 * restricted role that manages question/game content ONLY. Its tier is `0`
 * so it satisfies NO tier guard (not even `viewer`) — it must never inherit
 * the dashboards, economy, user-moderation, config, or audit surfaces. Its
 * content access is granted exclusively through an explicit role-allowlist
 * (`CONTENT_MANAGEMENT_ROLES` / `canManageContent`), NOT through the ladder.
 * Reviewers MUST reject any code that gates a non-content surface on
 * `content_manager` or that relies on tier math to admit it.
 */
export type AdminUserRole =
  | 'content_manager'
  | 'viewer'
  | 'admin'
  | 'superadmin';

/**
 * Numeric ordering for tier comparisons. Higher tier satisfies lower-tier
 * guards. `content_manager` is `0` on purpose: it sits below `viewer` on the
 * ladder so tier guards never admit it — its privileges come from
 * `CONTENT_MANAGEMENT_ROLES`, not from this number.
 */
export const ADMIN_USER_ROLE_TIER: Record<AdminUserRole, number> = {
  content_manager: 0,
  viewer: 1,
  admin: 2,
  superadmin: 3,
};

export const ADMIN_USER_ROLES: readonly AdminUserRole[] = [
  'viewer',
  'content_manager',
  'admin',
  'superadmin',
] as const;

/**
 * Roles permitted to manage question / game content (create, edit, review,
 * AI-assist, list, detail). This is the role-allowlist that the content
 * routes and SPA content pages gate on — NOT a tier comparison. `admin` and
 * `superadmin` are included so the existing higher tiers keep their content
 * access; `content_manager` is the lateral role whose ENTIRE remit is this
 * set. `viewer` is intentionally absent (read-only viewers never wrote
 * content). Read-only question quality metrics are a separate, broader set
 * (any authenticated admin) — see the route gating.
 */
export const CONTENT_MANAGEMENT_ROLES: readonly AdminUserRole[] = [
  'content_manager',
  'admin',
  'superadmin',
] as const;

/** True when `role` may manage question / game content. */
export function canManageContent(role: AdminUserRole): boolean {
  return CONTENT_MANAGEMENT_ROLES.includes(role);
}

/** JWT audience claim — distinguishes player tokens from admin tokens. */
export type JwtAudience = 'player' | 'admin';

/**
 * Admin login success response.
 *
 * `token` is the short-lived (1h) admin access JWT (`aud: 'admin'`).
 *
 * Refresh token transport: HttpOnly cookie `wc_admin_refresh`, set by the
 * server when the client opted in via `rememberMe`. The cookie is NEVER in
 * this JSON payload — it lives in `Set-Cookie` so JS can't pull it via XSS.
 * Cookie TTL: 24 h without Remember Me, 30 d with. Without a cookie the
 * client must re-authenticate (password + MFA) when the access token expires.
 */
export interface AdminLoginSuccessResponse {
  kind: 'authenticated';
  token: string;
  adminUser: {
    id: string;
    username: string;
    displayName: string;
    role: AdminUserRole;
    mfaEnrolled: boolean;
  };
  /** True when the server issued a refresh cookie alongside this response. */
  rememberMe: boolean;
  /** Absolute expiry of the access token (UNIX ms) — for client-side renewal scheduling. */
  accessTokenExpiresAt: number;
}

/**
 * Same shape as `AdminLoginSuccessResponse`, returned by `/admin-refresh`
 * after a refresh-token rotation. Aliased for clarity at the call sites.
 */
export type AdminRefreshResponse = AdminLoginSuccessResponse;

/**
 * Admin login response when the server requires a TOTP challenge. Client
 * must follow up with POST /api/auth/admin-login/mfa carrying the
 * challenge token + 6-digit code (or a recovery code).
 */
export interface AdminLoginMfaChallengeResponse {
  kind: 'mfa_required';
  mfaChallengeToken: string; // short-lived (5 min) token, opaque to the client
  /** True if the admin still needs to complete first-time MFA enrollment. */
  enrollmentRequired: boolean;
}

export type AdminLoginResponse =
  | AdminLoginSuccessResponse
  | AdminLoginMfaChallengeResponse;

/** Response shape when an admin enrolls in TOTP MFA. Shown ONCE. */
export interface AdminMfaEnrollmentResponse {
  /** `otpauth://` URI ready to drop into a QR-code renderer. */
  otpauthUri: string;
  /** Base-32 secret, in case the user wants to enter it manually. */
  secret: string;
  /**
   * 10 one-time recovery codes. Shown ONCE, never again. The server stores
   * only the hashes. Caller MUST persist these somewhere (download/print).
   */
  recoveryCodes: string[];
}

// ---------------------------------------------------------------------------
// Audit log (server/src/models/AuditLog.ts)
// ---------------------------------------------------------------------------

/**
 * One admin audit trail entry. Used by GET /api/admin/audit responses.
 * `before`/`after` are arbitrary JSON snapshots; null means create (no before)
 * or delete (no after). `ip` and `userAgent` may be null for system-generated
 * entries with no HTTP context.
 */
export interface AuditLogEntry {
  id: string;
  actorId: string;
  /**
   * Human-readable handle of the actor, resolved from the actorId at read
   * time. Actors may be admins (`AdminUser`) OR players (`User`, e.g. the
   * self-service `user.username.rename` action), so the server resolves
   * across both collections. `null` when the actor row no longer exists
   * (e.g. a deleted admin) — render the raw `actorId` as a fallback.
   */
  actorUsername: string | null;
  /** Display name of the actor when available (AdminUser.displayName). `null` otherwise. */
  actorDisplayName: string | null;
  action: string;
  targetType: string;
  targetId: string;
  before: unknown;
  after: unknown;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Admin economy summary (GET /api/admin/economy/summary)
// ---------------------------------------------------------------------------

/**
 * One aggregated bucket for a (day, currency, type) tuple.
 * `credits` is the sum of positive-amount transactions (money flowing in).
 * `debits` is the sum of absolute values of negative-amount transactions (money
 * flowing out). `count` is the number of transactions in this bucket.
 */
export interface EconomySummaryBucket {
  day: string; // ISO date: "2026-04-17"
  currency: Currency;
  type: TransactionType;
  credits: number;
  debits: number;
  count: number;
}

/** Admin economy summary response. `currency` echoes the filter; 'all' = no filter. */
export interface EconomySummary {
  from: string; // ISO date (inclusive lower bound)
  to: string;   // ISO date (inclusive upper bound)
  currency: Currency | 'all';
  buckets: EconomySummaryBucket[];
}

// ---------------------------------------------------------------------------
// Admin leaderboard snapshot (GET /api/admin/leaderboard/snapshot)
// ---------------------------------------------------------------------------

/** One ranked entry in an admin leaderboard snapshot. */
export interface LeaderboardSnapshotEntry {
  rank: number;
  userId: string;
  displayName: string;
  wins: number;
  coins: number;
}

/** Admin leaderboard snapshot response — current top-N players. */
export interface LeaderboardSnapshot {
  entries: LeaderboardSnapshotEntry[];
  generatedAt: string; // ISO timestamp
  limit: number;
}

// ---------------------------------------------------------------------------
// Admin dashboard metrics (GET /api/admin/dashboard/metrics)
// ---------------------------------------------------------------------------

/** A `{ key, count }` breakdown row used for type/difficulty/platform splits. */
export interface DashboardBreakdownEntry {
  key: string;
  count: number;
}

/** Player-account metrics. Bots are excluded from every count here. */
export interface DashboardUserMetrics {
  /** Total non-bot users. */
  total: number;
  guests: number;
  social: number;
  banned: number;
  /** New non-bot signups in the last 24 hours / 7 days (by `createdAt`). */
  newLast24h: number;
  newLast7d: number;
  /** Bot accounts (informational; excluded from the counts above). */
  bots: number;
}

/** Live-activity metrics sourced from Redis (presence + matchmaking) and Mongo. */
export interface DashboardLiveMetrics {
  /**
   * Real players with a live presence key right now — bots subtracted.
   * Bots share the same Redis presence keyspace (they have no sockets, so
   * their schedule is materialized into it), which is why this is a
   * difference rather than a raw key count.
   */
  onlineUsers: number;
  /** Pool bots whose schedule currently has them online. Operational only. */
  onlineBots: number;
  /** Matches currently `in_progress`. */
  activeMatches: number;
  /** Users waiting in the matchmaking queue. */
  matchmakingQueue: number;
}

/** In-app-purchase metrics from the `iapTransactions` collection. */
export interface DashboardIapMetrics {
  /** All non-refunded/non-revoked transactions (granted + verified). */
  total: number;
  refunded: number;
  /**
   * Sum of `priceUsd` over live PRODUCTION transactions only. Gross catalog
   * list price — not net of store commission or currency conversion, and
   * sandbox/TestFlight purchases are excluded entirely (they cost the tester
   * nothing).
   */
  revenueUsd: number;
  /** Live transactions from a tester channel — excluded from `revenueUsd`. */
  sandboxCount: number;
  /** Live rows predating the `environment` field; also excluded from revenue. */
  unknownEnvCount: number;
  countLast24h: number;
  countLast7d: number;
  byPlatform: DashboardBreakdownEntry[];
}

/** One player waiting in the matchmaking queue (admin live drill-down). */
export interface AdminMatchmakingQueueEntry {
  userId: string;
  username: string;
  displayName: string;
  /**
   * Defence-in-depth: structurally always `false` today. Only the
   * authenticated socket handler calls `joinQueue`, and bot fallback goes
   * through `initiateMatch` without ever touching the queue key — so a bot
   * cannot currently appear here. Kept (and rendered) so that if a future
   * path ever does enqueue one, the operator sees it rather than reading a
   * bot as player demand.
   */
  isBot: boolean;
  language: Language;
  mode: string;
  totalWins: number;
  /** When the player joined the queue (ISO). */
  joinedAt: string;
  /** How long they have been waiting, at response time. */
  waitingMs: number;
}

/** GET /api/admin/matchmaking/queue */
export interface AdminMatchmakingQueueResponse {
  entries: AdminMatchmakingQueueEntry[];
  /**
   * Queue size straight from Redis. May exceed `entries.length` when an entry
   * references a user row that no longer exists — surfaced rather than hidden
   * so the number can be reconciled with the dashboard card.
   */
  total: number;
}

/** One IAP transaction row in the admin purchases list. */
export interface AdminIapTransaction {
  id: string;
  userId: string;
  /** Null when the buyer's account has since been deleted. */
  username: string | null;
  platform: 'apple' | 'google';
  productId: string;
  transactionId: string;
  priceUsd: number;
  /** Store environment — `sandbox` rows are excluded from revenue. */
  environment: 'production' | 'sandbox' | 'unknown';
  status: 'verified' | 'granted' | 'refunded' | 'revoked';
  grantedAt: string;
  refundedAt: string | null;
  createdAt: string;
}

/**
 * Status filter for the purchases list. `live` is the granted+verified pair
 * the dashboard's revenue and transaction cards count — it exists so a card
 * can link to a list scoped exactly as the card was counted.
 */
export type AdminIapStatusFilter =
  | 'live'
  | 'verified'
  | 'granted'
  | 'refunded'
  | 'revoked';

/** GET /api/admin/iap/transactions */
export interface AdminIapTransactionsResponse {
  data: AdminIapTransaction[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  /**
   * Sum of `priceUsd` over PRODUCTION rows matching the current filter, in
   * LIVE statuses (granted + verified) unless the operator asked for a
   * specific non-live status. Refunded money is not revenue, for the same
   * reason sandbox is not.
   */
  productionRevenueUsd: number;
}

/**
 * Admin dashboard metrics response — the platform sections. A game adds its
 * own (Word Clash: `WordClashDashboardMetrics.questions`).
 */
export interface DashboardMetrics {
  generatedAt: string; // ISO timestamp
  users: DashboardUserMetrics;
  live: DashboardLiveMetrics;
  iap: DashboardIapMetrics;
  firstSession: DashboardFirstSessionMetrics;
}

/**
 * First-session funnel over the last `windowDays` (#764). Organic humans
 * only — bots and seed fixtures are excluded. Every stage counts the cohort
 * CREATED in the window (distinct players). `returnedNextDay` = active 24 h
 * or more after signup, judged only for players whose first 24 h are over.
 */
export interface DashboardFirstSessionMetrics {
  windowDays: number;
  signedUp: number;
  startedMatch: number;
  madeFirstMove: number;
  backgroundedInMatch: number;
  leftMatch: number;
  completedMatch: number;
  returnedNextDay: number;
  /** Median ms from match start to the first move in a player's FIRST match. */
  medianFirstMoveMs: number | null;
}

/** POST /api/telemetry/funnel body (#764). */
export interface FunnelEventInput {
  name: FunnelEventName;
  matchId?: string;
  /** ms since the match started on this client. */
  elapsedMs?: number;
}

// ---------------------------------------------------------------------------
// Admin users management (GET/POST /api/admin/users/...)
// ---------------------------------------------------------------------------

/** Minimal user shape returned from GET /api/admin/users search listings. */
export interface UserSearchResult {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  authProvider: AuthProvider;
  avatarId: string;
  language: Language;
  isBanned: boolean;
  isBot: boolean;
  /**
   * Live presence, read from Redis at request time — NOT derived from
   * `lastActiveAt`, which is a stale Mongo mirror. Bots report their scheduled
   * presence here like any other row; the admin surface shows identity, so
   * suppressing it would misrepresent who is connected.
   */
  isOnline: boolean;
  totalWins: number;
  totalMatches: number;
  createdAt: string; // ISO timestamp
  lastActiveAt: string; // ISO timestamp
}

/** Paginated response for GET /api/admin/users. */
export interface UserSearchResponse {
  data: UserSearchResult[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

/** Recent match entry in admin user detail — swapped to this user's perspective. */
export interface AdminUserRecentMatch {
  id: string;
  /** For an N-seat table: the best-placed other player. */
  opponentId: string;
  opponentName: string;
  /** N-seat tables only: this user's placement and the table size. */
  placement?: number | null;
  playerCount?: number;
  mode: GameMode;
  status: MatchStatus;
  yourScore: number;
  opponentScore: number;
  winnerId: string | null;
  startedAt: string; // ISO timestamp
  endedAt: string | null; // ISO timestamp
}

/**
 * Full admin detail for a user. Sensitive fields (password, tokenVersion,
 * firebaseUid, authProviderId) are deliberately omitted — admins should not
 * need them and they MUST NOT leak through the admin API. `tokenVersion` is
 * surfaced only via the ban/unban/currency response shapes, not here.
 */
/**
 * One device an account has signed in or played from (#device-tracking).
 *
 * Admin-only. `lastIp` in particular MUST NOT be exposed on any
 * player-facing payload — this type is referenced only by
 * `UserDetailResponse`, which is served exclusively behind
 * `requireAdminRole('admin')`.
 */
export interface AdminUserDevice {
  /**
   * SHA-256 of the device id, never the raw value.
   *
   * The raw device id is a BEARER CREDENTIAL — `POST /auth/guest`
   * authenticates on it alone — so surfacing it here would hand every
   * `admin`-tier operator (and anyone seeing a URL, a devtools dump or a
   * support screenshot) an account-takeover primitive against every guest.
   * The hash serves the pivot identically: paste it into the users search to
   * find every account that has used the device.
   */
  deviceIdHash: string;
  /** `'unknown'` for clients predating the `X-Device-*` headers. */
  platform: 'ios' | 'android' | 'unknown';
  osVersion: string | null;
  appVersion: string | null;
  lastIp: string | null;
  lastAuthProvider: string | null;
  firstSeenAt: string; // ISO timestamp
  lastSeenAt: string; // ISO timestamp
  /** Authentication events from this device — not requests, not reconnects. */
  loginCount: number;
  /**
   * Distinct accounts that have used this device, INCLUDING this one. A
   * value above 1 is the multi-accounting / ban-evasion signal; search the
   * users list by the device id to see the others.
   */
  sharedWithAccountCount: number;
}

export interface UserDetailResponse {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  authProvider: AuthProvider;
  avatarId: string;
  language: Language;
  coins: number;
  gems: number;
  energy: number;
  maxEnergy: number;
  energyLastRefill: string; // ISO timestamp
  unlimitedEnergyExpires: string | null; // ISO timestamp
  noAdsExpires: string | null; // ISO timestamp
  soundEnabled: boolean;
  musicEnabled: boolean;
  notificationsEnabled: boolean;
  totalWins: number;
  totalLosses: number;
  totalMatches: number;
  totalCorrectAnswers: number;
  isBanned: boolean;
  isBot: boolean;
  /** Live Redis presence at request time (see `UserSearchResult.isOnline`). */
  isOnline: boolean;
  botSkillTier: string | null;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  lastActiveAt: string; // ISO timestamp
  recentMatches: AdminUserRecentMatch[];
  /**
   * Devices this account has signed in or played from, most recent first.
   * Empty for accounts whose client never reported a device id — which is
   * every social login from a binary predating the device headers, so an
   * empty list means "not observed", never "no devices".
   */
  devices: AdminUserDevice[];
}

/** POST /api/admin/users/:id/ban body (all fields optional). */
export interface BanUserRequest {
  reason?: string;
}

/**
 * POST /api/admin/users/:id/ban response. `tokenVersion` is deliberately NOT
 * surfaced — it's an internal JWT revocation counter and leaking it through
 * the admin API exposes auth-implementation detail with no consumer benefit
 * (the admin UI only needs to know the ban succeeded).
 */
export interface BanUserResponse {
  id: string;
  isBanned: true;
}

/** POST /api/admin/users/:id/unban response. */
export interface UnbanUserResponse {
  id: string;
  isBanned: false;
}

/** POST /api/admin/users/:id/currency body. */
export interface CurrencyAdjustRequest {
  coinsDelta?: number;
  gemsDelta?: number;
  energyDelta?: number;
  reason: string;
}

/** POST /api/admin/users/:id/currency response — final balances post-adjustment. */
export interface CurrencyAdjustResponse {
  id: string;
  coins: number;
  gems: number;
  energy: number;
  maxEnergy: number;
}

/**
 * One transaction row surfaced by GET /api/admin/users/:id/transactions.
 * Mirrors the persisted `Transaction` doc — `metadata` is arbitrary JSON
 * captured at credit/debit time (e.g. matchId, power-up type).
 */
export interface AdminUserTransaction {
  id: string;
  type: TransactionType;
  currency: Currency;
  amount: number;
  balanceAfter: number;
  metadata: Record<string, unknown> | null;
  createdAt: string; // ISO timestamp
}

/** Paginated response for GET /api/admin/users/:id/transactions. */
export interface AdminUserTransactionsResponse {
  data: AdminUserTransaction[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Admin account management (GET/POST /api/admin/admin-users/...)
//
// Operates on the `AdminUser` collection ONLY. Strictly distinct from the
// player-facing admin endpoints above (`/api/admin/users/*` manages players).
// ---------------------------------------------------------------------------

/** List row for GET /api/admin/admin-users. */
export interface AdminAccountListItem {
  id: string;
  username: string;
  displayName: string;
  role: AdminUserRole;
  isDisabled: boolean;
  mfaEnrolled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Paginated response for GET /api/admin/admin-users. */
export interface AdminAccountListResponse {
  data: AdminAccountListItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

/** POST /api/admin/admin-users body. */
export interface CreateAdminAccountRequest {
  username: string;
  displayName: string;
  role: AdminUserRole;
  password: string;
}

/** PATCH /api/admin/admin-users/:id body — partial, all fields optional. */
export interface UpdateAdminAccountRequest {
  displayName?: string;
  role?: AdminUserRole;
  isDisabled?: boolean;
}

/** POST /api/admin/admin-users/:id/reset-password body. */
export interface ResetAdminPasswordRequest {
  password: string;
}

// ---------------------------------------------------------------------------
// Admin matches management (GET/POST /api/admin/matches/...) (#286)
// ---------------------------------------------------------------------------

/**
 * Friendly filter names accepted by GET /api/admin/matches?status=.
 * Mapped to underlying MatchStatus values server-side:
 *   active    → [Matchmaking, InProgress]
 *   completed → [Completed]
 *   forfeit   → [Abandoned]
 */
/**
 * `active` groups Matchmaking + InProgress; `in_progress` is InProgress ALONE,
 * which is what the dashboard's "Active games" card counts. The card links to
 * the narrow one so the number and the list it opens agree.
 */
export type AdminMatchStatusFilter =
  | 'active'
  | 'in_progress'
  | 'completed'
  | 'forfeit';

/** List-item shape for GET /api/admin/matches. Keep small — detail page fetches the full doc. */
/**
 * One seat of an N-seat table as the admin panel sees it (epic #679). Unlike
 * the player-facing standings this is admin-only, so `replacedByBot` may name
 * that a bot played a seat out.
 */
export interface AdminMatchSeat {
  seat: number;
  userId: string;
  team: number | null;
  placement: number | null;
  score: number;
  replacedByBot: boolean;
}

export interface AdminMatchListItem {
  id: string;
  mode: GameMode;
  status: MatchStatus;
  language: string;
  player1Id: string;
  player2Id: string;
  player1Score: number;
  player2Score: number;
  winnerId: string | null;
  startedAt: string; // ISO timestamp
  endedAt: string | null; // ISO timestamp
  /**
   * N-seat tables only. When present, `player1Id` / `player2Id` name seats 0
   * and 1 and the scores are theirs, so an older admin build still renders a
   * row; a table-aware build renders `seats`.
   */
  seats?: AdminMatchSeat[];
}

export interface AdminMatchListResponse {
  data: AdminMatchListItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Admin — DM Moderation (report triage + auto-filter flags)
// ---------------------------------------------------------------------------

/** Minimal user summary embedded in moderation rows for display. */
export interface AdminModerationUserSummary {
  id: string;
  username: string;
  displayName: string;
  avatarId: string | null;
  isBanned: boolean;
}

/** One user-submitted report in the admin moderation queue. */
export interface AdminModerationReportItem {
  id: string;
  reason: ModerationReportReason;
  notes: string | null;
  status: 'pending' | 'reviewed' | 'dismissed';
  reporter: AdminModerationUserSummary | null;
  reportedUser: AdminModerationUserSummary | null;
  conversationId: string;
  messageId: string;
  /** Reported message body as stored (already masked if the filter masked it).
   *  `null` if the message has since been purged (TTL) or soft-deleted. */
  messageText: string | null;
  reviewedAt: string | null; // ISO
  reviewedByDisplayName: string | null;
  createdAt: string; // ISO
}

export interface AdminModerationReportListResponse {
  data: AdminModerationReportItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface ResolveModerationReportResponse {
  id: string;
  status: 'reviewed' | 'dismissed';
}

/** One auto-filter event (masked or blocked message) in the flagged view. */
export interface AdminModerationFlagItem {
  id: string;
  action: 'masked' | 'blocked';
  sender: AdminModerationUserSummary | null;
  conversationId: string;
  /** `null` for blocked messages (never persisted as a Message). */
  messageId: string | null;
  /** The original, unmasked text that tripped the filter. */
  originalText: string;
  /** The masked rendering that was delivered (null for blocked). */
  maskedText: string | null;
  matchedTokens: string[];
  language: Language;
  createdAt: string; // ISO
}

export interface AdminModerationFlagListResponse {
  data: AdminModerationFlagItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

/** One message in a report's surrounding-conversation context view. */
export interface AdminModerationContextMessage {
  id: string;
  senderId: string;
  senderDisplayName: string;
  text: string | null;
  isReportedMessage: boolean;
  /** True if the message was soft-deleted (e.g. sender self-moderated or a
   *  block hid it). Shown in the admin context view so reviewers can tell
   *  self-removed messages from live ones. */
  isDeleted: boolean;
  createdAt: string; // ISO
}

export interface AdminModerationReportContextResponse {
  conversationId: string;
  reportedMessageId: string;
  messages: AdminModerationContextMessage[];
}

/**
 * Populated player summary shown in admin match detail. Kept minimal so the
 * admin UI can render a badge / avatar without fetching the full User doc.
 */
export interface AdminMatchPlayerSummary {
  id: string;
  username: string;
  displayName: string;
  avatarId: string | null;
}

/**
 * Live (Redis-held) state of an in-progress match. Populated only when the
 * match is in `matchmaking` or `in_progress` and a round is active. `null` if
 * the match is stale/orphaned or already completed.
 */
export interface AdminMatchLiveState {
  currentRound: number;
  currentTurn: 'player1' | 'player2' | null;
  turnDeadline: number | null; // ms epoch
  player1Score: number;
  player2Score: number;
  acceptedPlayers: string[]; // user IDs that accepted the preview
}

/** Full detail returned by GET /api/admin/matches/:id. */
export interface AdminMatchDetail {
  id: string;
  mode: GameMode;
  status: MatchStatus;
  player1: AdminMatchPlayerSummary;
  player2: AdminMatchPlayerSummary;
  player1Score: number;
  player2Score: number;
  winnerId: string | null;
  totalRounds: number;
  language: string;
  startedAt: string; // ISO timestamp
  endedAt: string | null; // ISO timestamp
  /** Redis-held live round state; null when the match is completed/abandoned/stale. */
  liveState: AdminMatchLiveState | null;
  /** N-seat tables only — every seat with its player (see AdminMatchListItem.seats). */
  seats?: (AdminMatchSeat & { player: AdminMatchPlayerSummary })[];
}

/** Error response */
export interface ApiError {
  error: string;
  message: string;
  statusCode: number;
}

// ---------------------------------------------------------------------------
// Remote App Configuration (Epic #336)
// Server-owned, mobile-subscribed. Lives as a singleton Mongo doc. See
// `docs/ARCHITECTURE.md` → "Remote App Configuration".
// ---------------------------------------------------------------------------

/** Per-platform minimum supported app version (plain MAJOR.MINOR.PATCH). */
export interface AppConfigMinimumVersion {
  ios: string;
  android: string;
}

/**
 * Ad mediation provider the mobile client initializes at boot. Both SDKs
 * (AppLovin MAX + Google AdMob) ship in the binary; this remote toggle picks
 * which one runs — switching is a config change, no app release. `'none'`
 * disables ad SDK initialization entirely (formats degrade gracefully).
 * The provider is locked in for the session once an SDK has initialized;
 * a flip applies on the next app launch.
 */
export type AdProvider = 'applovin' | 'admob' | 'none';

/**
 * Ad configuration: provider selection + per-format kill switches.
 * Disabled formats skip SDK calls entirely regardless of provider.
 */
export interface AppConfigAds {
  /** Which ad SDK serves this session. Failsafe default: 'admob'. */
  provider: AdProvider;
  banner: { enabled: boolean };
  interstitial: { enabled: boolean };
  rewarded: { enabled: boolean };
}

/**
 * Maintenance mode blocks new matchmaking and shows a gate screen; live matches
 * finish naturally. `messageKey` is an i18n key resolved on the client; plain
 * text is NOT passed through for localisation reasons.
 */
export interface AppConfigMaintenance {
  enabled: boolean;
  messageKey?: string;
  startsAt?: string;
  endsAt?: string;
}

/** Global kill switch — emergency stop that shows the ServiceUnavailable gate. */
export interface AppConfigKillSwitch {
  global: boolean;
}

/**
 * Observability kill switches. `crashlyticsEnabled` is a remote stop on Firebase
 * Crashlytics ingestion (#429). Defaults to `true` (failsafe ON) — a config
 * fetch failure must NOT silently break crash telemetry. Flipping this to
 * `false` disables crash collection on the next client config refresh.
 */
export interface AppConfigObservability {
  crashlyticsEnabled: boolean;
  /**
   * First-session funnel telemetry (#764) kill switch. Absent = enabled.
   */
  funnelTelemetryEnabled?: boolean;
}

/**
 * Which side of the gameplay answer feed the CURRENT player's bubbles align
 * to. `'right'` matches the messaging-app convention (own messages on the
 * right — the same side the in-app DM thread uses); `'left'` is the legacy
 * pre-flip layout, kept remotely selectable so the change can be reverted
 * without an app release. Presentation-only: the opponent always renders on
 * the opposite side, and no server logic reads this value.
 */
export type AnswerFeedSide = 'left' | 'right';

/**
 * Gameplay presentation toggles. Client-rendering knobs that need no app
 * build to change — the server stores and serves them but never acts on them.
 */
export interface AppConfigGameplay {
  /** Side the current player's answer bubbles align to. Failsafe: 'right'. */
  answerFeedSelfSide: AnswerFeedSide;
}

/**
 * Which Home screen layout the client renders. `'classic'` is the shipped
 * Home; `'hero'` is the shared hero layout (game art in an arch, PLAY bar on
 * its edge) — rendered only where the game registers `branding.homeHero`, in
 * portrait, on a light theme, so the flag can never select a layout the game
 * cannot draw. Presentation-only: no server logic reads this value.
 */
export type HomeLayout = 'classic' | 'hero';

/**
 * Shell presentation toggles, per game. Like `gameplay`, the server stores
 * and serves these but never acts on them.
 */
export interface AppConfigUi {
  /** Home screen layout. Failsafe: 'classic'. */
  homeLayout: HomeLayout;
}

/**
 * Live-tunable energy economy. Lets ops adjust the free-to-play energy gate
 * (raise the cap, speed up regen, lower match cost) from the admin config UI
 * without shipping an app build — energy is server-authoritative, so the
 * effective values are read from here on every energy computation, with the
 * `ENERGY.*` constants as the failsafe default. Defaults mirror the constants.
 */
export interface AppConfigEconomy {
  /** Regen ceiling for ALL users (passive-regen cap). Default 10. */
  maxEnergy: number;
  /** Passive regeneration interval in minutes. Default 10. */
  regenIntervalMinutes: number;
  /** Energy new accounts start with. Default 10. */
  startingEnergy: number;
  /** Energy spent to start a match. Default 1 (0 = no energy gate). */
  matchCostPerMatch: number;
  /**
   * Wins required in one UTC day to fire the daily-challenge bonus. Default
   * 3 (`COINS.DAILY_BONUS_REQUIRED_WINS`). Live-tunable because the target
   * interacts with the energy gate: at a ~50% win rate, N wins costs ~2N
   * matches, so a target the energy cap cannot fund in a day is a retention
   * mechanic nobody can finish. Minimum 1 — a 0 target would fire on every
   * settled match.
   *
   * Client surfaces (`DailyChallengeCard`, HomeScreen) MUST read this rather
   * than the constant, or the progress bar renders a goal the server does
   * not award at.
   */
  dailyChallengeRequiredWins: number;
  /**
   * Base coins for a settled match, per outcome. Live-tunable and PER GAME
   * because a game's economy is its own: Backgammon Clash charges 2 energy
   * a match and is designed to pay 150/40, while the incumbent pays 50/10.
   * Before these existed the reward path read the Word Clash constants
   * directly, so backgammon paid a third of its designed reward for double
   * the energy and its own WIN_COINS/LOSS_COINS constants were dead code.
   */
  matchWinCoins: number;
  matchLossCoins: number;
  /**
   * Energy granted by ONE rewarded-ad watch. Live-tunable and PER GAME for
   * the same reason as the base match rewards above: the grant is only
   * meaningful relative to that game's match cost. Backgammon Clash charges
   * 2⚡ a match and is designed to pay 4⚡ an ad, so an ad funds two matches
   * exactly as it does for the incumbent's 1⚡/2⚡ pair — with the frozen
   * `ENERGY.AD_REWARD_AMOUNT` constant an ad funded HALF a session there.
   *
   * This is a FREE energy source, so the grant still caps at `maxEnergy`
   * (`07-game-design-rules.md` §"Energy Cap Scope") — only PAID grants
   * overflow.
   */
  adEnergyRewardAmount: number;
}

/**
 * Live remote configuration for the mobile app. `version` is monotonic and
 * bumped on every mutation. Clients treat `{ version }` as the cache key in
 * ETag headers and in the `config_updated` WebSocket event.
 */
export interface AppConfig {
  version: number;
  updatedAt: string;
  minimumVersion: AppConfigMinimumVersion;
  ads: AppConfigAds;
  maintenance: AppConfigMaintenance;
  killSwitch: AppConfigKillSwitch;
  observability: AppConfigObservability;
  economy: AppConfigEconomy;
  gameplay: AppConfigGameplay;
  ui: AppConfigUi;
}

// ---------------------------------------------------------------------------
// Admin Broadcast Notifications
// Admin-composed push notifications fanned out to segments of the userbase
// via Firebase Cloud Messaging. History is persisted for audit + retry.
// ---------------------------------------------------------------------------

export type BroadcastTargetType = 'all' | 'platform';
export type BroadcastStatus = 'pending' | 'sending' | 'completed' | 'failed';
export type DevicePlatform = 'ios' | 'android';

export interface SendBroadcastRequest {
  title: string;
  body: string;
  targetType: BroadcastTargetType;
  /** Only required when targetType === 'platform'. */
  platform?: DevicePlatform;
  /** Optional payload available to the client on tap (e.g. deep-link keys). */
  data?: Record<string, string>;
}

export interface BroadcastListItem {
  id: string;
  title: string;
  body: string;
  targetType: BroadcastTargetType;
  platform?: DevicePlatform;
  status: BroadcastStatus;
  recipientCount: number;
  successCount: number;
  failureCount: number;
  createdBy: { id: string; username: string };
  createdAt: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface BroadcastListResponse {
  items: BroadcastListItem[];
  page: number;
  limit: number;
  total: number;
}

// =============================================================================
// Support Messages (Settings → Contact Us)
// =============================================================================

export type SupportMessageStatus = 'open' | 'resolved';

export interface SupportMessageItem {
  id: string;
  userId: string;
  username: string;
  subject: string;
  body: string;
  language: string;
  clientInfo: {
    platform: string | null;
    appVersion: string | null;
    osVersion: string | null;
  } | null;
  status: SupportMessageStatus;
  resolvedAt: string | null;
  resolvedBy: string | null;
  adminNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessagesListResponse {
  data: SupportMessageItem[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface UpdateSupportMessageRequest {
  status?: SupportMessageStatus;
  adminNote?: string | null;
}
